import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Loader2, Users, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { tApi, fmtDateShort } from "@/lib/tournaments";

import { useSeo } from "@/lib/seo";
export default function RankingsPage() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [teams, setTeams] = useState(null);
  const [athletes, setAthletes] = useState(null);

  useSeo({ title: "Rankings — Beach Volley Greece", description: "Season rankings of beach volleyball athletes competing in The Sand Lab tournaments in Chania, Greece.", path: "/rankings" });
  useEffect(() => { tApi.get("/rankings/years").then((r) => setYears(r.data)); }, []);
  useEffect(() => {
    const q = year ? `?year=${year}` : "";
    setTeams(null); setAthletes(null);
    tApi.get(`/rankings/teams${q}`).then((r) => setTeams(r.data));
    tApi.get(`/rankings/athletes${q}`).then((r) => setAthletes(r.data));
  }, [year]);

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="rankings-page">
      <Navbar />
      <header className="bg-ocean text-white pt-28 pb-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-sunset mb-3">Μόνιμη κατάταξη</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-none">THE SAND LAB RANKINGS</h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">Οι πόντοι κατάταξης συγκεντρώνονται από όλα τα τουρνουά. 1η: 160 · 2η: 120 · 3η–4η: 100 · 5η–8η: 60.</p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Link to="/tournaments" className="text-sm font-bold text-ocean hover:underline" data-testid="rankings-tournaments-link">← Tournaments</Link>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
            Σεζόν
            <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="rankings-year" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm">
              <option value="">Όλες</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>
        <Tabs defaultValue="teams">
          <TabsList className="bg-white border border-slate-100 rounded-2xl p-1 shadow-soft">
            <TabsTrigger value="teams" data-testid="tab-rank-teams" className="rounded-xl font-bold data-[state=active]:bg-ocean data-[state=active]:text-white"><Users className="h-4 w-4 mr-2" />Team Ranking</TabsTrigger>
            <TabsTrigger value="athletes" data-testid="tab-rank-athletes" className="rounded-xl font-bold data-[state=active]:bg-ocean data-[state=active]:text-white"><User className="h-4 w-4 mr-2" />Individual Ranking</TabsTrigger>
          </TabsList>
          <TabsContent value="teams" className="mt-6"><RankTable rows={teams} kind="team" /></TabsContent>
          <TabsContent value="athletes" className="mt-6"><RankTable rows={athletes} kind="athlete" /></TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}

function RankTable({ rows, kind }) {
  const [openId, setOpenId] = useState(null);
  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-ocean" /></div>;
  if (rows.length === 0) return <div className="rounded-3xl bg-white border border-slate-100 p-10 text-center text-slate-500" data-testid={`rank-${kind}-empty`}>Δεν υπάρχουν ακόμη πόντοι κατάταξης. Η κατάταξη ενημερώνεται μετά την οριστικοποίηση κάθε τουρνουά.</div>;
  return (
    <div className="rounded-3xl bg-white border border-slate-100 shadow-soft overflow-hidden">
      <table className="w-full text-sm" data-testid={`rank-${kind}-table`}>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b bg-slate-50/60">
            <th className="py-3 px-4 w-14">#</th><th className="py-3 px-4">{kind === "team" ? "Ομάδα" : "Αθλήτρια"}</th>
            <th className="py-3 px-4 text-center">Τουρνουά</th><th className="py-3 px-4 text-center">Πόντοι</th><th className="py-3 px-4 text-center hidden sm:table-cell">Καλύτερη θέση</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <>
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`rank-row-${r.id}`}>
                <td className="py-3 px-4 font-heading font-black text-ocean">{r.position <= 3 ? <Trophy className={`inline h-4 w-4 mr-1 ${r.position === 1 ? "text-amber-500" : r.position === 2 ? "text-slate-400" : "text-amber-700"}`} /> : null}{r.position}</td>
                <td className="py-3 px-4 font-bold text-slate-900">
                  {kind === "athlete" ? <Link to={`/athletes/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-ocean" data-testid={`athlete-link-${r.id}`}>{r.name}</Link> : r.name}
                </td>
                <td className="py-3 px-4 text-center tabular-nums">{r.tournaments}</td>
                <td className="py-3 px-4 text-center font-heading font-black text-lg text-slate-900">{r.points}</td>
                <td className="py-3 px-4 text-center hidden sm:table-cell">{r.best?.label || "—"}</td>
              </tr>
              {openId === r.id && (
                <tr key={`${r.id}-h`} className="bg-slate-50/60"><td colSpan={5} className="px-6 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Ιστορικό</div>
                  <ul className="space-y-1 text-sm">
                    {r.history.map((h, i) => (
                      <li key={i} className="flex flex-wrap justify-between gap-2">
                        <span><Link to={`/tournaments/${h.slug}`} className="font-semibold text-ocean hover:underline">{h.title}</Link> <span className="text-slate-500">· {fmtDateShort(h.date)}{h.partner ? ` · με ${h.partner}` : ""}{kind === "athlete" ? ` · ${h.team_name}` : ""}</span></span>
                        <span className="font-bold">{h.placement} · {h.points} π.</span>
                      </li>
                    ))}
                  </ul>
                </td></tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}
